import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { joinWaitlist, fetchWaitlistStatus } from "@/lib/api";
import Seo from "@/components/Seo";
import { MandalaMotif } from "@/components/MotifBackground";
import {
  Sparkles, Flame, BookOpen, HeartHandshake, Flower2, Users, MapPin, Star,
  CalendarDays, Clock, Bot, Plus, Check, Loader2, Heart, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const SOURCE = "dharmartha_premium";
const WKEY = "dh_premium_waitlisted";

const FEATURES = [
  { icon: Flame, en: "Personalised Sadhana Guide", hi: "वैयक्तिक साधना मार्गदर्शिका",
    desc_en: "A personalised approach to daily Sadhana, prayers, practices and spiritual discipline.",
    desc_hi: "दैनिक साधना, प्रार्थना, अभ्यास एवं आध्यात्मिक अनुशासन हेतु एक वैयक्तिक मार्ग।" },
  { icon: BookOpen, en: "Exclusive Content", hi: "विशिष्ट सामग्री",
    desc_en: "Deeper explorations of Sanatana Dharma, scriptures, traditions, temples, rituals and spiritual practices.",
    desc_hi: "सनातन धर्म, शास्त्र, परंपरा, मंदिर, अनुष्ठान एवं आध्यात्मिक अभ्यासों का गहन अन्वेषण।" },
  { icon: HeartHandshake, en: "Seva Opportunities", hi: "सेवा अवसर",
    desc_en: "Discover verified opportunities to support temples, Gau Seva, Annadan, heritage preservation, education and other Dharmic initiatives.",
    desc_hi: "मंदिर, गौ सेवा, अन्नदान, धरोहर संरक्षण, शिक्षा एवं अन्य धार्मिक कार्यों हेतु सत्यापित सेवा अवसर।" },
  { icon: Flower2, en: "Pooja & Anushthan Guides", hi: "पूजा एवं अनुष्ठान मार्गदर्शिका",
    desc_en: "Step-by-step guidance for Poojas, Vratas, Anushthans and festival observances, with Vidhi, Mantras and Samagri lists.",
    desc_hi: "पूजा, व्रत, अनुष्ठान एवं पर्व हेतु चरण-दर-चरण मार्गदर्शन — विधि, मंत्र एवं सामग्री सूची सहित।" },
  { icon: Users, en: "Live & Community Events", hi: "लाइव एवं सामुदायिक आयोजन",
    desc_en: "Participate in live Satsangs, spiritual sessions, discussions, workshops and community gatherings.",
    desc_hi: "लाइव सत्संग, आध्यात्मिक सत्र, चर्चा, कार्यशालाओं एवं सामुदायिक आयोजनों में सहभागिता।" },
  { icon: MapPin, en: "Pilgrimage Planning", hi: "तीर्थ यात्रा नियोजन",
    desc_en: "Plan meaningful pilgrimages with guidance on sacred places, temples, traditions, rituals and practical arrangements.",
    desc_hi: "पवित्र स्थलों, मंदिरों, परंपराओं, अनुष्ठानों एवं व्यावहारिक व्यवस्थाओं के मार्गदर्शन सहित सार्थक तीर्थ यात्रा का नियोजन।" },
  { icon: Star, en: "Yearly Astrology Report", hi: "वार्षिक ज्योतिष रिपोर्ट",
    desc_en: "A detailed annual Vedic astrology report offering insights into the year ahead.",
    desc_hi: "आगामी वर्ष की अंतर्दृष्टि प्रदान करती विस्तृत वार्षिक वैदिक ज्योतिष रिपोर्ट।" },
  { icon: CalendarDays, en: "Advanced Panchangam", hi: "उन्नत पंचांग",
    desc_en: "Go beyond the everyday Panchangam with deeper access to Tithi, Nakshatra, Yoga, Karana, Muhurta and other elements of Vedic timekeeping.",
    desc_hi: "तिथि, नक्षत्र, योग, करण, मुहूर्त एवं वैदिक कालगणना के अन्य तत्वों तक गहन पहुँच के साथ दैनिक पंचांग से आगे।" },
  { icon: Clock, en: "Muhurta Consultation", hi: "मुहूर्त परामर्श",
    desc_en: "Guidance in identifying auspicious timings for important ceremonies, beginnings and significant occasions.",
    desc_hi: "महत्वपूर्ण संस्कारों, आरंभों एवं विशेष अवसरों हेतु शुभ मुहूर्त निर्धारण में मार्गदर्शन।" },
  { icon: Bot, en: "Dharmartha AI", hi: "धर्मार्थ AI",
    desc_en: "An intelligent companion for exploring Sanatana Dharma, scriptures, traditions, rituals, temples, mantras, festivals and spiritual practices.",
    desc_hi: "सनातन धर्म, शास्त्र, परंपरा, अनुष्ठान, मंदिर, मंत्र, पर्व एवं आध्यात्मिक अभ्यासों के अन्वेषण हेतु एक बुद्धिमान सहचर।" },
];

function Waitlist() {
  const { pick } = useLang();
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    try { if (localStorage.getItem(WKEY) === "1") setDone(true); } catch (e) { /* ignore */ }
    fetchWaitlistStatus(SOURCE).then(({ waitlisted }) => { if (waitlisted) setDone(true); }).catch(() => {});
  }, []);

  const submit = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error(pick("मान्य ईमेल दर्ज करें", "Enter a valid email")); return; }
    setBusy(true);
    try {
      await joinWaitlist({ email, name, source: SOURCE });
      try { localStorage.setItem(WKEY, "1"); } catch (e) { /* ignore */ }
      setDone(true);
      toast(pick("धन्यवाद! आप प्रतीक्षा-सूची में हैं।", "Thank you! You're on the waitlist."));
    } catch (e) {
      toast.error(pick("जोड़ नहीं सके", "Could not join"), { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="premium-waitlist-done" className="border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.08)] p-7 text-center md:p-10">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[var(--gold-muted)] bg-surface text-saffron">
          <Check className="h-7 w-7" />
        </span>
        <h3 className="mt-5 font-serif-latin text-2xl text-ink">{pick("आप प्रतीक्षा-सूची में हैं", "You're on the waitlist")}</h3>
        <p className="mx-auto mt-2 max-w-lg text-[15px] leading-relaxed text-ink-2">
          {pick("धर्मार्थ प्रीमियम आरंभ होते ही हम आपको सूचित करेंगे। तब तक, आप दान देकर हमारा सहयोग कर सकते हैं।",
                "We'll let you know as soon as we launch. In the meantime, you can support us by donating.")}
        </p>
        <Link to="/support" data-testid="premium-donate-cta" className="mt-6 inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
          <Heart className="h-4 w-4" /> {pick("सहयोग करें", "Support Dharmartha")}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="premium-waitlist-form" className="border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.06)] p-7 md:p-10">
      <p className="overline-label text-gold">{pick("प्रतीक्षा-सूची", "Waitlist")}</p>
      <h3 className="mt-2 font-serif-latin text-2xl text-ink md:text-3xl">
        {pick("धर्मार्थ प्रीमियम का अनुभव सर्वप्रथम करें।", "Be among the first to experience Dharmartha Premium.")}
      </h3>
      <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
        <input data-testid="premium-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={pick("आपका नाम (वैकल्पिक)", "Your name (optional)")}
          className="w-full border border-line bg-parchment px-3.5 py-3 text-sm text-ink focus:border-saffron focus:outline-none" />
        <input data-testid="premium-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={pick("ईमेल पता", "Email address")}
          className="w-full border border-line bg-parchment px-3.5 py-3 text-sm text-ink focus:border-saffron focus:outline-none" />
      </div>
      <button data-testid="premium-waitlist-submit" onClick={submit} disabled={busy}
        className="mt-4 inline-flex items-center gap-2 bg-[var(--saffron)] px-7 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {pick("प्रतीक्षा-सूची में जुड़ें", "Join the Waitlist")}
      </button>
    </div>
  );
}

export default function Premium() {
  const { pick } = useLang();
  return (
    <div data-testid="premium-page">
      <Seo path="/premium" title="Dharmartha Premium — Coming Soon"
        description="Dharmartha Premium will bring together personalised spiritual guidance, deeper knowledge, meaningful experiences and thoughtfully designed tools to help you nurture your spiritual journey." />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-line bg-[var(--surface)]">
        <MandalaMotif className="pointer-events-none absolute -right-24 -top-16 h-[28rem] w-[28rem] text-[var(--gold)] opacity-[0.07]" />
        <div className="relative z-[2] mx-auto max-w-4xl px-5 py-20 text-center md:px-8 md:py-28">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--gold-muted)] bg-surface/70 px-3.5 py-1.5 text-xs font-medium tracking-wide text-gold" data-testid="premium-soon-badge">
            <Sparkles className="h-3.5 w-3.5" /> {pick("शीघ्र आ रहा है", "Coming Soon")}
          </span>
          <h1 className="mt-5 font-serif-latin text-4xl leading-tight text-ink md:text-6xl" data-testid="premium-title">
            {pick("धर्मार्थ प्रीमियम", "Dharmartha Premium")}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl font-serif-latin text-xl italic text-gold md:text-2xl">
            {pick("अपने धर्म को सीखने, आचरण करने एवं जीने का एक गहन मार्ग।", "A deeper way to learn, practice and live your Dharma.")}
          </p>
          <p className="mx-auto mt-6 max-w-2xl text-[17px] leading-relaxed text-ink-2">
            {pick(
              "धर्मार्थ प्रीमियम वैयक्तिक आध्यात्मिक मार्गदर्शन, गहन ज्ञान, सार्थक अनुभव एवं सोच-समझकर बनाए गए साधनों को एक साथ लाएगा — ताकि आप अपनी आध्यात्मिक यात्रा को पोषित कर सकें।",
              "Dharmartha Premium will bring together personalised spiritual guidance, deeper knowledge, meaningful experiences and thoughtfully designed tools to help you nurture your spiritual journey."
            )}
          </p>
        </div>
      </section>

      {/* What's Coming */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
        <div className="mb-10 text-center">
          <p className="overline-label text-gold">{pick("शीघ्र आगमन", "What's Coming")}</p>
          <h2 className="mt-2 font-serif-latin text-3xl text-ink md:text-4xl">{pick("क्या-क्या आ रहा है", "What's Coming")}</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.en} data-testid={`premium-feature-${f.en.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group flex flex-col border border-line bg-surface p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[var(--gold)]/50 hover:shadow-[0_12px_40px_rgba(44,38,33,0.08)]">
              <span className="flex h-11 w-11 items-center justify-center border border-[var(--gold-muted)] text-saffron transition-colors group-hover:bg-[var(--saffron)] group-hover:text-white">
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-serif-latin text-xl text-ink">{pick(f.hi, f.en)}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-2">{pick(f.desc_hi, f.desc_en)}</p>
            </div>
          ))}
          {/* And More */}
          <div className="flex flex-col justify-center border border-dashed border-[var(--gold-muted)] bg-[rgba(176,141,87,0.05)] p-6" data-testid="premium-feature-and-more">
            <span className="flex h-11 w-11 items-center justify-center border border-[var(--gold-muted)] text-gold">
              <Plus className="h-5 w-5" />
            </span>
            <h3 className="mt-4 font-serif-latin text-xl text-ink">{pick("और भी बहुत कुछ…", "And More…")}</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-2">
              {pick("धर्म से आपके जुड़ाव को गहरा करने वाले और भी साधन, अनुभव एवं मार्ग सोच-समझकर विकसित किए जा रहे हैं।",
                    "More tools, experiences and ways to deepen your connection with Dharma are being thoughtfully developed.")}
            </p>
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <section className="mx-auto max-w-4xl px-5 pb-20 md:px-8" data-testid="premium-waitlist-section">
        <Waitlist />
      </section>
    </div>
  );
}
